import React from 'react';

/**
 * LabReportModal — inline report viewer. Renders the already-fetched report
 * blob directly in the page (image or PDF) instead of opening a new tab, so it
 * can't be killed by a popup blocker. "Open in new tab" / "Download" are plain
 * anchors fired by a direct user click, so they're gesture-allowed.
 */
export default function LabReportModal({
  open, loading, error, blobUrl, contentType, fileName, onClose,
}) {
  if (!open) return null;
  const isImage = (contentType || '').startsWith('image/');
  const linkCls =
    'rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 px-3 py-1.5 text-sm font-semibold hover:bg-slate-100 dark:hover:bg-slate-700';

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000, padding: '1rem',
        background: 'rgba(15,23,42,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white dark:bg-slate-800 rounded-xl shadow-xl"
        style={{ width: '100%', maxWidth: '56rem', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}
      >
        <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-slate-200 dark:border-slate-700">
          <div className="font-bold text-slate-800 dark:text-slate-100 truncate">{fileName || 'Lab report'}</div>
          <div className="flex items-center gap-2 shrink-0">
            {blobUrl && (
              <>
                <a href={blobUrl} target="_blank" rel="noopener noreferrer" className={linkCls}>Open in new tab</a>
                <a href={blobUrl} download={fileName || 'lab-report'} className={linkCls}>Download</a>
              </>
            )}
            <button type="button" onClick={onClose} className={linkCls}>Close</button>
          </div>
        </div>
        <div className="p-3 overflow-auto" style={{ flex: 1, minHeight: '16rem' }}>
          {loading && (
            <div className="text-center py-12 text-slate-500 dark:text-slate-400">Loading report…</div>
          )}
          {!loading && error && (
            <div className="text-center py-12 text-rose-600 dark:text-rose-300">{error}</div>
          )}
          {!loading && !error && blobUrl && (
            isImage ? (
              <img src={blobUrl} alt={fileName || 'Lab report'} style={{ maxWidth: '100%', display: 'block', margin: '0 auto' }} />
            ) : (
              <iframe title="Lab report" src={blobUrl} style={{ width: '100%', height: '70vh', border: 'none' }} />
            )
          )}
        </div>
      </div>
    </div>
  );
}
